import React from 'react';
import {
  Box,
  Container,
  Typography,
  Avatar,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Star as StarIcon,
} from '@mui/icons-material';
import { copyToClipboard, openInNewTab } from '../utils/profileUtils';
import { REFERRAL_URL_FALLBACK } from '../constants/profileConstants';

const ReferralBanner = ({ userData, onCopySuccess }) => {
  const theme = useTheme();
  const referralUrl = userData?.referral_url || REFERRAL_URL_FALLBACK;

  const handleCopy = async () => {
    const success = await copyToClipboard(referralUrl);
    if (success) {
      onCopySuccess();
    }
  };

  const handleShare = () => {
    openInNewTab(referralUrl);
  };

  return (
    <Box sx={{
      width: '100%',
      background: theme.palette.mode === 'dark'
        ? `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.15)}, ${alpha(theme.palette.secondary.dark, 0.08)})`
        : `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.08)}, ${alpha(theme.palette.secondary.light, 0.04)})`,
      borderBottom: theme.palette.mode === 'dark'
        ? `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`
        : `1px solid ${alpha(theme.palette.divider, 0.15)}`,
      py: { xs: 1.5, sm: 2 },
      px: { xs: 2, sm: 3 },
      position: 'relative',
      overflow: 'hidden',
      backdropFilter: 'blur(10px)',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: theme.palette.mode === 'dark'
          ? `linear-gradient(90deg, transparent, ${alpha(theme.palette.secondary.main, 0.1)}, transparent)`
          : `linear-gradient(90deg, transparent, ${alpha(theme.palette.secondary.main, 0.05)}, transparent)`,
        backgroundSize: '200% 100%',
        animation: 'shimmer 3s ease-in-out infinite',
      },
      '@keyframes shimmer': {
        '0%': { backgroundPosition: '-200% 0' },
        '100%': { backgroundPosition: '200% 0' }
      }
    }}>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 3 }
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flex: 1
          }}>
            <Avatar
              sx={{
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                bgcolor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.secondary.main, 0.25)
                  : alpha(theme.palette.secondary.main, 0.12),
                color: theme.palette.mode === 'dark'
                  ? theme.palette.secondary.light
                  : theme.palette.secondary.main,
                boxShadow: theme.palette.mode === 'dark'
                  ? `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}`
                  : `0 2px 8px ${alpha(theme.palette.secondary.main, 0.15)}`,
              }}
            >
              <StarIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  color: theme.palette.text.primary,
                  mb: 0.5,
                  textShadow: theme.palette.mode === 'dark'
                    ? `0 1px 2px ${alpha('#000', 0.3)}`
                    : 'none'
                }}
              >
                🎉 Referral Program
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.8rem', sm: '0.85rem' },
                  display: { xs: 'none', sm: 'block' },
                  opacity: theme.palette.mode === 'dark' ? 0.9 : 0.8
                }}
              >
                Share your link and earn rewards when friends join!
              </Typography>
            </Box>
          </Box>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            flexDirection: { xs: 'column', sm: 'row' },
            width: { xs: '100%', md: 'auto' }
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.95)
                : alpha(theme.palette.background.paper, 0.9),
              borderRadius: 1.5,
              px: 2,
              py: 1,
              border: theme.palette.mode === 'dark'
                ? `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`
                : `1px solid ${alpha(theme.palette.divider, 0.25)}`,
              minWidth: { xs: '100%', sm: '280px', md: '320px' },
              maxWidth: { xs: '100%', sm: '400px' },
              boxShadow: theme.palette.mode === 'dark'
                ? `0 2px 8px ${alpha('#000', 0.4)}`
                : `0 1px 4px ${alpha('#000', 0.05)}`,
              backdropFilter: 'blur(8px)'
            }}>
              <Typography
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                  fontFamily: 'monospace',
                  color: theme.palette.text.primary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  fontWeight: theme.palette.mode === 'dark' ? 500 : 400
                }}
              >
                {referralUrl}
              </Typography>
            </Box>

            <Box sx={{
              display: 'flex',
              gap: 1,
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCopy}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 1.5,
                  px: { xs: 2, sm: 2.5 },
                  py: 0.8,
                  fontSize: '0.8rem',
                  borderColor: theme.palette.mode === 'dark'
                    ? theme.palette.secondary.light
                    : theme.palette.secondary.main,
                  color: theme.palette.mode === 'dark'
                    ? theme.palette.secondary.light
                    : theme.palette.secondary.main,
                  flex: { xs: 1, sm: 'none' },
                  backdropFilter: 'blur(8px)',
                  '&:hover': {
                    borderColor: theme.palette.mode === 'dark'
                      ? theme.palette.secondary.main
                      : theme.palette.secondary.dark,
                    backgroundColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.secondary.main, 0.08)
                      : alpha(theme.palette.secondary.main, 0.04),
                    boxShadow: theme.palette.mode === 'dark'
                      ? `0 4px 12px ${alpha(theme.palette.secondary.main, 0.2)}`
                      : `0 2px 8px ${alpha(theme.palette.secondary.main, 0.15)}`,
                  }
                }}
              >
                📋 Copy
              </Button>

              <Button
                variant="contained"
                size="small"
                onClick={handleShare}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 1.5,
                  px: { xs: 2, sm: 2.5 },
                  py: 0.8,
                  fontSize: '0.8rem',
                  flex: { xs: 1, sm: 'none' },
                  background: theme.palette.mode === 'dark'
                    ? `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`
                    : `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                  color: theme.palette.mode === 'dark'
                    ? theme.palette.common.white
                    : theme.palette.common.white,
                  boxShadow: theme.palette.mode === 'dark'
                    ? `0 4px 12px ${alpha(theme.palette.secondary.main, 0.4)}`
                    : `0 2px 8px ${alpha(theme.palette.secondary.main, 0.25)}`,
                  '&:hover': {
                    background: theme.palette.mode === 'dark'
                      ? `linear-gradient(45deg, ${theme.palette.secondary.dark}, ${theme.palette.secondary.main})`
                      : `linear-gradient(45deg, ${theme.palette.secondary.dark}, ${theme.palette.secondary.main})`,
                    boxShadow: theme.palette.mode === 'dark'
                      ? `0 6px 16px ${alpha(theme.palette.secondary.main, 0.5)}`
                      : `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}`,
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                🚀 Share
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ReferralBanner;

